import { Component, inject } from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { ActivatedRoute, RouterModule } from '@angular/router'
import { DialogService } from '@ziptility/components/dialogs'
import { Routine, RoutineService } from '@ziptility/routines'
import { RoutineSelectorComponent, RoutineListItemComponent } from '../routines/routine-selector'
import { IconsModule } from '@ziptility/icons'
import { MatMenuModule } from '@angular/material/menu'
import { AssetProfileService } from '@ziptility/feature/asset-profile.service'
import { firstValueFrom } from 'rxjs'

@Component({
  selector: 'feature-routines',
  template: `
    <div class="flex flex-col items-stretch h-full">
      <div class="px-12 py-8 flow-grow h-full">
        @if (!vm.selectedRoutines().length) {
          <div class="bg-gray-300 h-[365px] flex items-center justify-center">
            <div class="text-center flex flex-col items-center justify-center">
              <i-feather name="clipboard"></i-feather>
              <div class="font-semibold text-lg">No Routines</div>
              <div class="text-sm text-gray-600">Routines automatically create Tasks on a recurring schedule.</div>
            </div>
          </div>
        }
        @for (routine of vm.selectedRoutines(); track routine) {
          <div class="flex justify-between items-center border-gray-500 border-b">
            <routine-list-item [routine]="routine" class="w-full"></routine-list-item>
            <mat-menu #actions>
              <div class="bv-menu">
                <div class="bv-menu-item" [routerLink]="['../../../', 'routines', routine.id]" [relativeTo]="route">
                  View details
                </div>
                <div
                  class="bv-menu-item"
                  [routerLink]="['../../../', 'routines']"
                  [queryParams]="{ edit: routine.id }"
                  [relativeTo]="route"
                >
                  Edit
                </div>
                <div class="bv-menu-item " (click)="onRemoveRoutine(routine)">
                  <span class="text-red-brand">Remove from Routine</span>
                </div>
              </div>
            </mat-menu>
            <div class="p-2" [mat-menu-trigger-for]="actions">
              <i-feather name="more-horizontal" class="feather-icon-sm"></i-feather>
            </div>
          </div>
        }
      </div>
      <div class="p-5 py-4 border-t border-gray-200">
        <button class="bv-submit-button w-full" (click)="onAddRoutine()">Add to Routine</button>
      </div>
    </div>
  `,
  imports: [IconsModule, MatMenuModule, RouterModule, RoutineListItemComponent],
})
export class FeatureRoutinesComponent {
  readonly routineService = inject(RoutineService)
  readonly route = inject(ActivatedRoute)
  readonly dialogService = inject(DialogService)
  readonly matDialog = inject(MatDialog)
  readonly vm = inject(AssetProfileService)
  routineSelectorModalRef: MatDialogRef<RoutineSelectorComponent>

  ngOnDestroy() {
    this.routineSelectorModalRef?.close()
  }

  async onAddRoutine() {
    const { organizationId, mapId, featureId } = this.route.snapshot.params
    this.routineSelectorModalRef = this.matDialog.open(RoutineSelectorComponent, {
      width: '600px',
      panelClass: 'mat-dialog-no-padding',
      data: {
        organizationId,
        mapId,
        routines: this.vm.selectableRoutines(),
      },
    })
    const selection = await firstValueFrom(this.routineSelectorModalRef.afterClosed())
    if (!selection) {
      return
    }
    try {
      await firstValueFrom(this.routineService.addFeaturesToRoutine(organizationId, mapId, selection.id, [featureId]))
      this.vm.loadRoutines(organizationId, mapId, featureId)
    } catch (err) {
      console.log(err)
    }
  }

  private async confirmRemove(routine: Routine): Promise<boolean> {
    return new Promise((resolve) => {
      this.dialogService.confirmDialog(
        `Are you sure?`,
        `This will remove the routine '${routine.title}' from this asset. This action cannot be undone.`,
        'Remove',
        'warning',
        undefined,
        (err, response) => {
          if (response) {
            resolve(true)
          }
        },
      )
    })
  }

  async onRemoveRoutine(routine: Routine) {
    const { organizationId, mapId, featureId } = this.route.snapshot.params
    const confirmed = await this.confirmRemove(routine)
    if (!confirmed) {
      return
    }

    try {
      await firstValueFrom(this.routineService.removeFeatureFromRoutine(organizationId, mapId, routine.id, featureId))
      this.vm.loadRoutines(organizationId, mapId, featureId)
    } catch (err) {
      console.warn(err)
    }
  }
}
